/**
 * 411 (DECISIONS 411; DIRECTION §0.14 step 4 (b)): the enclosed seats' eye — a driver's and a passenger's, inside a cab or a
 * hull — with the owner's pick off lane E's sheet (design_sheets/seat_insides.html): "(A) everywhere", a few faces a seat.
 * Hulls are modelled outside-in, so from inside their faces are culled and the eye saw through its own vehicle (or into the
 * pickup's dark glass). Each seat here: where its eye sits (the hull's frame, model units at 1.8 a person) and the inside
 * round it — the pickup's cab (the technical's, the supply truck's), a buttoned-up driver's station round its vision blocks
 * (the 6×6's, the M113's, the tank's), a bay (the 6×6's, the M113's) — one merged mesh, its colours in its vertices: one draw.
 * The inside and the own vehicle (its glass discarded: see-through from inside) are drawn in the viewmodel's pass over the
 * world, each with its own depth (rendering/viewmodel.ts drawSeatInside): the hull's faces near the eye never cut through it.
 * Presentation only; behind the eye's Lab row (game/eyeView.ts).
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

type V3 = [number, number, number];
type Pt = [number, number];
const rect = (x0: number, y0: number, x1: number, y1: number): Pt[] => [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];

/** The colours inside: dark trim, a lighter liner, the seats, the armour's painted inside. */
export const SEAT_INSIDE_COLORS = { trim: '#3c3d41', liner: '#8f897c', seat: '#5c4e3e', dash: '#2a2b30', armour: '#6c7a71', deck: '#525b56', frame: '#18191c', lamp: '#d8d0b0' };
const IN = SEAT_INSIDE_COLORS;

function tint(g: THREE.BufferGeometry, color: string): THREE.BufferGeometry {
  const c = new THREE.Color(color), n = g.attributes.position.count, a = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) a.set([c.r, c.g, c.b], i * 3);
  g.setAttribute('color', new THREE.BufferAttribute(a, 3));
  return g;
}
/** A flat face: `outline` (and `holes`) in its plane, its x along `u` and its y along `v`, from `o`. */
function face(out: THREE.BufferGeometry[], color: string, o: V3, u: V3, v: V3, outline: Pt[], holes: Pt[][] = []): void {
  const s = new THREE.Shape(outline.map(([x, y]) => new THREE.Vector2(x, y)));
  for (const h of holes) s.holes.push(new THREE.Path(h.map(([x, y]) => new THREE.Vector2(x, y))));
  const g = new THREE.ShapeGeometry(s);
  const U = new THREE.Vector3(...u).normalize(), V = new THREE.Vector3(...v).normalize(), N = new THREE.Vector3().crossVectors(U, V);
  g.applyMatrix4(new THREE.Matrix4().makeBasis(U, V, N).setPosition(...o));
  out.push(tint(g, color));
}
function block(out: THREE.BufferGeometry[], color: string, size: V3, at: V3, rx = 0): void {
  const g = new THREE.BoxGeometry(...size); if (rx) g.rotateX(rx); g.translate(...at); out.push(tint(g, color));
}
function ringPart(out: THREE.BufferGeometry[], color: string, r: number, tube: number, at: V3, rx: number): void {
  const g = new THREE.TorusGeometry(r, tube, 4, 14); g.rotateX(rx); g.translate(...at); out.push(tint(g, color));
}
function merged(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const g = mergeGeometries(parts.map(p => {
    const q = p.index ? p.toNonIndexed() : p;
    q.deleteAttribute('uv');
    return q;
  }))!;
  g.computeVertexNormals();
  return g;
}

/** The pickup's cab (TECHNICAL_MODEL's measures): the floor, the liner, the back wall round its window, the windscreen's frame,
 *  the dash, the wheel on the driver's side, the bench. The doors' insides are the model's own (their slabs face in). */
function pickupCab(): THREE.BufferGeometry {
  const out: THREE.BufferGeometry[] = [], W = 0.8, sill = 0.58, roof = 1.84, back = -0.03, screenBase = 1.22, roofFront = 0.7, hoodTop = 1.2;
  face(out, IN.trim, [-W, sill, back], [1, 0, 0], [0, 0, 1], rect(0, 0, 2 * W, screenBase - back));
  face(out, IN.liner, [-W - 0.04, roof, back], [1, 0, 0], [0, 0, 1], rect(0, 0, 2 * W + 0.08, roofFront + 0.02 - back));
  face(out, IN.trim, [-W, sill, back], [1, 0, 0], [0, 1, 0], rect(0, 0, 2 * W, roof - sill), [rect(0.08, 0.87, 2 * W - 0.08, 1.17)]);
  const len = Math.hypot(screenBase - roofFront, roof + 0.02 - hoodTop);
  face(out, IN.trim, [-W, hoodTop, screenBase], [1, 0, 0], [0, roof + 0.02 - hoodTop, roofFront - screenBase], rect(0, 0, 2 * W, len), [rect(0.1, 0.07, 2 * W - 0.1, len - 0.06)]);
  block(out, IN.dash, [2 * W - 0.04, 0.16, 0.3], [0, 1.1, 1.06]);
  block(out, IN.trim, [0.5, 0.05, 0.22], [0.42, 1.19, 1.05]);
  ringPart(out, IN.dash, 0.18, 0.018, [0.45, 1.16, 0.8], -0.45);
  block(out, IN.dash, [0.04, 0.04, 0.3], [0.45, 1.12, 0.93], -0.45);
  block(out, IN.seat, [1.5, 0.14, 0.5], [0, 0.72, 0.3]);
  block(out, IN.seat, [1.5, 0.55, 0.1], [0, 1.07, 0.04], 0.12);
  return merged(out);
}

/** A buttoned-up driver's station round the eye `e` at the periscopes' top: the walls, the blocks cut in its front (each its
 *  centre across and its width) with their lips, the lid's underside, the panel, the sticks or the 6×6's wheel. */
function driverStation(e: V3, blocks: Array<[number, number]>, wheel: boolean): THREE.BufferGeometry {
  const out: THREE.BufferGeometry[] = [], [x, y, z] = e, hw = 0.42, down = 0.7, up = 0.14, back = 0.42, ahead = 0.24, bh = 0.075;
  face(out, IN.armour, [x - hw, y - down, z + ahead], [1, 0, 0], [0, 1, 0], rect(0, 0, 2 * hw, down + up),
    blocks.map(([cx, w]) => rect(hw + cx - w / 2, down - 0.02, hw + cx + w / 2, down - 0.02 + bh)));
  for (const [cx, w] of blocks) face(out, IN.frame, [x + cx - w / 2 - 0.02, y - 0.04, z + ahead - 0.004], [1, 0, 0], [0, 1, 0], rect(0, 0, w + 0.04, bh + 0.04), [rect(0.02, 0.02, w + 0.02, bh + 0.02)]);
  face(out, IN.armour, [x - hw, y - down, z - back], [0, 0, 1], [0, 1, 0], rect(0, 0, back + ahead, down + up));
  face(out, IN.armour, [x + hw, y - down, z - back], [0, 0, 1], [0, 1, 0], rect(0, 0, back + ahead, down + up));
  face(out, IN.liner, [x - hw, y + up, z - back], [1, 0, 0], [0, 0, 1], rect(0, 0, 2 * hw, back + ahead));
  face(out, IN.trim, [x - hw, y - down, z - back], [1, 0, 0], [0, 1, 0], rect(0, 0, 2 * hw, down + up));
  face(out, IN.deck, [x - hw, y - down, z - back], [1, 0, 0], [0, 0, 1], rect(0, 0, 2 * hw, back + ahead));
  block(out, IN.dash, [2 * hw - 0.06, 0.16, 0.14], [x, y - 0.2, z + ahead - 0.08]);
  for (const sx of [-0.08, 0.08]) block(out, IN.dash, [0.03, 0.03, 0.06], [x + sx * 2, y - 0.14, z + ahead - 0.16]);
  if (wheel) ringPart(out, IN.dash, 0.16, 0.016, [x, y - 0.3, z + 0.02], -0.6);
  else for (const sx of [-0.14, 0.14]) block(out, IN.dash, [0.03, 0.36, 0.03], [x + sx, y - 0.42, z + 0.04], -0.35);
  return merged(out);
}

interface Bay { half: number; floor: number; waist: number; roof: number; roofHalf: number; front: number; rear: number; bench: number; blocks: Array<[number, number]>; benchX: number; benchZ: number[] }
/** A bay: its floor, walls (the 6×6's sloped above its waist, its vision blocks cut), roof (a hatch's seams, the dome lamp),
 *  the front bulkhead, the rear door, the benches along both walls. */
function bayHull(b: Bay): THREE.BufferGeometry {
  const out: THREE.BufferGeometry[] = [], L = b.front - b.rear;
  face(out, IN.deck, [-b.half, b.floor, b.rear], [1, 0, 0], [0, 0, 1], rect(0, 0, 2 * b.half, L));
  face(out, IN.liner, [-b.roofHalf, b.roof, b.rear], [1, 0, 0], [0, 0, 1], rect(0, 0, 2 * b.roofHalf, L));
  for (const side of [-1, 1]) {
    face(out, IN.armour, [side * b.half, b.floor, b.rear], [0, 0, 1], [0, 1, 0], rect(0, 0, L, b.waist - b.floor));
    const dx = (b.roofHalf - b.half) * side, dy = b.roof - b.waist, sl = Math.hypot(dx, dy);
    if (sl > 1e-6) face(out, IN.armour, [side * b.half, b.waist, b.rear], [0, 0, 1], [dx, dy, 0], rect(0, 0, L, sl),
      b.blocks.map(([bz, at]) => rect(bz - b.rear - 0.15, at * sl - 0.05, bz - b.rear + 0.15, at * sl + 0.05)));
    block(out, IN.seat, [0.36, 0.08, L - 0.6], [side * (b.half - 0.2), b.floor + b.bench, (b.front + b.rear) / 2]);
    block(out, IN.seat, [0.06, 0.4, L - 0.6], [side * (b.half - 0.03), b.floor + b.bench + 0.32, (b.front + b.rear) / 2]);
  }
  for (const [z, c] of [[b.front, IN.trim], [b.rear, IN.armour]] as const) {
    face(out, c, [-b.half, b.floor, z], [1, 0, 0], [0, 1, 0], [[0, 0], [2 * b.half, 0], [2 * b.half, b.waist - b.floor], [b.half + b.roofHalf, b.roof - b.floor], [b.half - b.roofHalf, b.roof - b.floor], [0, b.waist - b.floor]]);
  }
  block(out, IN.frame, [0.6, 0.02, 0.02], [0, b.roof - 0.012, (b.front + b.rear) / 2 - 0.5]);
  block(out, IN.frame, [0.02, 0.02, 0.6], [0, b.roof - 0.012, (b.front + b.rear) / 2 - 0.5]);
  block(out, IN.lamp, [0.12, 0.03, 0.12], [0, b.roof - 0.02, (b.front + b.rear) / 2 + 0.3]);
  return merged(out);
}

const BTR_BAY: Bay = { half: 1.08, floor: 0.62, waist: 1.55, roof: 2.2, roofHalf: 0.88, front: 0.85, rear: -3.0, bench: 0.42, blocks: [[0.95, 0.75], [-0.45, 0.75], [-1.35, 0.75], [-2.25, 0.75]], benchX: 0.72, benchZ: [-1.45, -2.35] };
const M113_BAY: Bay = { half: 1.36, floor: 0.42, waist: 1.95, roof: 1.95, roofHalf: 1.36, front: -0.2, rear: -2.55, bench: 0.42, blocks: [], benchX: 0.95, benchZ: [-0.75, -1.3, -1.85, -2.4] };
/** A seated rider's eye over its bench's feet anchor (a person's seated eye at 1.8 a person). */
const RIDER_EYE = 1.15;

/** The drivers' eyes (the periscopes' top, just over the roof) and their blocks. */
const STATIONS = {
  btr: { eye: [-0.55, 2.34, 1.5] as V3, blocks: [[-0.22, 0.24], [0.06, 0.24]] as Array<[number, number]>, wheel: true },
  m113: { eye: [0.6, 2.08, 1.5] as V3, blocks: [[-0.22, 0.16], [0, 0.18], [0.22, 0.16]] as Array<[number, number]>, wheel: false },
  tank: { eye: [0.48, 1.5, 1.3] as V3, blocks: [[-0.22, 0.16], [0, 0.18], [0.22, 0.16]] as Array<[number, number]>, wheel: false },
};

export type SeatInsideShape = 'pickup' | 'btrStation' | 'm113Station' | 'tankStation' | 'btrBay' | 'm113Bay';
/** A seat's inside: which shape (one mesh a shape, shared by its seats), where its eye sits and which way it faces (the hull's
 *  frame); `driver`: the eye looks along the hull (the mouse steers), else the person's own look (a passenger looks about). */
export interface SeatInside { shape: SeatInsideShape; eye: V3; look: V3; driver: boolean }

/**
 * The inside of seat `index` of a body of `type` (its seats' roles, in order), or null where the eye has none (an open seat, the
 * tank's turret gunner — its sight is 69.54's — a bike, a mortar's crew). The technical's and the supply truck's cab, the 6×6
 * (`wheeled`), the M113 (`tracked`), the tank; a bay's riders on its benches by their order among the passengers.
 */
export function seatInsideFor(type: string, seats: ReadonlyArray<{ role: string }>, index: number): SeatInside | null {
  const role = seats[index]?.role;
  if (!role) return null;
  if (type === 'technical' || type === 'supplyTruck') {
    if (role === 'driver') return { shape: 'pickup', eye: [0.45, 1.52, 0.42], look: [0, 0, 1], driver: true };
    if (role === 'passenger') return { shape: 'pickup', eye: [-0.45, 1.52, 0.42], look: [0, 0, 1], driver: false };
    return null;
  }
  const station = type === 'wheeled' ? 'btr' : type === 'tracked' ? 'm113' : type === 'tank' ? 'tank' : null;
  if (!station) return null;
  if (role === 'driver') return { shape: `${station}Station` as SeatInsideShape, eye: STATIONS[station].eye, look: [0, 0, 1], driver: true };
  if (role !== 'passenger' || station === 'tank') return null;
  const bay = station === 'btr' ? BTR_BAY : M113_BAY;
  const nth = seats.slice(0, index).filter(s => s.role === 'passenger').length, side = nth % 2 === 0 ? 1 : -1;
  const z = bay.benchZ[Math.floor(nth / 2) % bay.benchZ.length];
  return { shape: `${station}Bay` as SeatInsideShape, eye: [side * bay.benchX, bay.floor + RIDER_EYE, z], look: [-side, 0, 0], driver: false };
}

const geometries = new Map<SeatInsideShape, THREE.BufferGeometry>();
/** A shape's geometry (built once). */
export function seatInsideGeometry(shape: SeatInsideShape): THREE.BufferGeometry {
  let g = geometries.get(shape);
  if (!g) {
    g = shape === 'pickup' ? pickupCab() : shape === 'btrBay' ? bayHull(BTR_BAY) : shape === 'm113Bay' ? bayHull(M113_BAY)
      : driverStation(STATIONS[shape === 'btrStation' ? 'btr' : shape === 'm113Station' ? 'm113' : 'tank'].eye, STATIONS[shape === 'btrStation' ? 'btr' : shape === 'm113Station' ? 'm113' : 'tank'].blocks, shape === 'btrStation');
    geometries.set(shape, g);
  }
  return g;
}
const insideMaterial = new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide, flatShading: true });
const meshes = new Map<SeatInsideShape, THREE.Mesh>();
/** A shape's mesh (one a shape: one seat's eye at a time), its matrix set by the pass from the hull's. */
export function seatInsideMesh(shape: SeatInsideShape): THREE.Mesh {
  let m = meshes.get(shape);
  if (!m) { m = new THREE.Mesh(seatInsideGeometry(shape), insideMaterial); m.matrixAutoUpdate = false; m.name = `seatInside ${shape}`; meshes.set(shape, m); }
  return m;
}

/** vehicleLooks' GLASS (linear, as the bake writes it into the vertex colours). */
const GLASS_LINEAR = new THREE.Color('#1c2328');
const glassless = new WeakMap<THREE.Material, THREE.Material>();
/**
 * A baked vehicle material's twin that drops the glass's fragments (its vertex colour the glass's): the own vehicle drawn from
 * inside, its windows see-through. One twin a material (the bake's are shared), compiled once.
 */
export function glasslessOf(mat: THREE.Material): THREE.Material {
  const lam = mat as THREE.MeshLambertMaterial;
  if (!lam.isMeshLambertMaterial || !lam.vertexColors) return mat;
  let twin = glassless.get(mat);
  if (!twin) {
    const t = lam.clone();
    t.onBeforeCompile = shader => {
      shader.uniforms.glassColor = { value: new THREE.Vector3(GLASS_LINEAR.r, GLASS_LINEAR.g, GLASS_LINEAR.b) };
      shader.fragmentShader = shader.fragmentShader
        .replace('void main() {', 'uniform vec3 glassColor;\nvoid main() {')
        .replace('#include <color_fragment>', '#include <color_fragment>\n\tif ( all( lessThan( abs( vColor.rgb - glassColor ), vec3( 0.004 ) ) ) ) discard;');
    };
    t.customProgramCacheKey = () => 'glassless';
    glassless.set(mat, t);
    twin = t;
  }
  return twin;
}
